// Runtime configuration. Pulled from Vite env vars so the same build can
// be deployed to dev / staging / prod with different `.env.production`
// files. Falls back to the production values used by the legacy frontend
// so the app keeps working when env vars are not provided.

const fromEnv = (key: string, fallback: string): string => {
  const v = import.meta.env[key];
  return typeof v === "string" && v.length > 0 ? v : fallback;
};

/** Google Maps 3D `Map3DElement.mode` values (see Maps JS API “map modes”). */
export const mapModes = ["SATELLITE", "HYBRID", "ROADMAP"] as const;
export type MapMode = (typeof mapModes)[number];

const fromEnvMapMode = (key: string, fallback: MapMode): MapMode => {
  const v = import.meta.env[key];
  return typeof v === "string" &&
    (mapModes as readonly string[]).includes(v.toUpperCase())
    ? (v.toUpperCase() as MapMode)
    : fallback;
};

export const config = {
  apiUrl: fromEnv("VITE_API_URL", "https://api.dwik.art/api/v1"),
  wsUrl: fromEnv("VITE_WS_URL", "wss://api.dwik.art/api/ws"),
  googleClientId: fromEnv(
    "VITE_GOOGLE_CLIENT_ID",
    "375083373289-knd5ku25bqdc4qicl5qdd54f0fti0vdi.apps.googleusercontent.com",
  ),
  googleMapsApiKey: fromEnv(
    "VITE_GOOGLE_MAPS_API_KEY",
    "AIzaSyCcvWNVKt_cV4obCh1FsPsFsvaodxDhHHw",
  ),
  googleAnalyticsId: fromEnv("VITE_GOOGLE_ANALYTICS_ID", "G-SMRF3CYFM4"),
  metaPixelId: fromEnv("VITE_META_PIXEL_ID", "1379695594279343"),
  /**
   * reCAPTCHA Enterprise policy-based challenge site key (public).
   * Create in Google Cloud Console → Security → reCAPTCHA with integration type
   * "policy-based-challenge". Challenge threshold is configured in the console.
   * Backend needs the same key plus RECAPTCHA_PROJECT_ID and a GCP API key.
   */
  recaptchaSiteKey: fromEnv("VITE_RECAPTCHA_SITE_KEY", "6LeegBUtAAAAAEZnQEhbHa7Rt_r2wQink_0R121e"),

  // Visual / 3D map defaults (mirrors the legacy config.js).
  strokeColor: "rgba(100, 100, 100, 1.0)",
  borderWidth: 2,
  fillColor: "rgba(200, 200, 200, 0.3)",
  fillOpacity: 0.5,

  /** Mesh base altitude (m) — legacy camera / terrain reference. */
  pixelMeshBaseAltitude: 175,
  /** Extra meters above mesh base for sold + hover/selection polygon overlays. */
  pixelOverlayAltitudeOffset: 100,

  /** Default 3D map rendering style (`SATELLITE`, `HYBRID`, or `ROADMAP`). */
  mapMode: fromEnvMapMode("VITE_MAP_MODE", "HYBRID"),
} as const;

export type AppConfig = typeof config;
